import React, { useRef } from "react";
import { HiDownload } from "react-icons/hi";
import { useReactToPrint } from "react-to-print";
import { Helmet } from "react-helmet-async";

const resumeData = {
  personalInfo: {
    firstName: "Gabriel Jesse",
    lastName: "Espinosa",
    location: "Los Angeles, CA",
    linkedin: "LinkedIn",
    website: "fullstackgabe.com",
    summary:
      "Full Stack Software Engineer with strong backend experience building scalable APIs, automation platforms, and internal engineering tools using Python, FastAPI, PostgreSQL, React, Redis, AWS, and Kubernetes.",
  },
  experience: [
    {
      company: "Canoga Perkins",
      location: "Los Angeles, CA",
      position: "Software Engineer - System Integration",
      startDate: "June 2022",
      endDate: "Present",
      description: [
        "Led backend development for a Network Management System that enables engineers to monitor, configure, and troubleshoot SONiC-based network devices across lab and production environments.",
        "Designed and implemented scalable FastAPI services with PostgreSQL to support device discovery, topology mapping, configuration management, workflow execution, and operational reporting.",
        "Architected REST API contracts consumed by React and TypeScript dashboards, enabling internal teams to trigger automation, track execution status, and visualize network state in near real time.",
        "Optimized PostgreSQL data models and queries using relational modeling, joins, indexes, pagination, and aggregation patterns to support large-scale filtering and dashboard analytics.",
        "Introduced Redis caching for high-read device and topology data, reducing redundant device queries and improving response times for frequently accessed endpoints.",
        "Built automation workflows for LLDP topology discovery, VLAN provisioning, PTP configuration, and device state collection using NETCONF, SSH, and REST API integrations.",
        "Strengthened backend reliability through pytest-based automated testing, structured logging, retries, timeout handling, validation, rate limiting, and defensive error handling to reduce operational failures.",
        "Collaborated with cross-functional engineering, QA, and network teams to investigate production issues, improve observability, and translate operational pain points into backend platform improvements.",
      ],
    },
    {
      company: "Abbott Laboratories",
      location: "Los Angeles, CA",
      position: "Software Engineer",
      startDate: "September 2021",
      endDate: "June 2022",
      description: [
        "Developed backend services in Python using FastAPI and PostgreSQL to support internal tools for device logging and FDA audit workflows.",
        "Built API endpoints to process, validate, and expose device and patient-related data across healthcare systems.",
        "Designed backend data flows for ingestion, storage, and retrieval of legacy device logs for compliance and traceability.",
        "Worked with distributed services to ensure accurate data flow and consistency across systems.",
        "Investigated system and data issues to improve backend reliability and audit data integrity.",
      ],
    },
    {
      company: "SQA Squared",
      location: "Los Alamitos, CA",
      position: "Software Automation Engineer",
      startDate: "May 2020",
      endDate: "May 2021",
      description: [
        "Developed automation frameworks to validate backend services and web applications across enterprise projects including GoodRx, Ticketmaster, and Consilio.",
        "Built API-level test suites to validate business logic, service behavior, and integration flows.",
        "Automated regression workflows for web applications, internal tools, and verification platforms.",
        "Integrated automated tests into CI/CD pipelines to improve release confidence and reduce manual validation effort.",
        "Worked with developers, QA teams, and product stakeholders to troubleshoot issues, document defects, and improve system stability.",
      ],
    },
  ],
  education: [
    {
      institution: "California State University, Channel Islands",
      location: "Camarillo, California",
      degree: "Bachelor’s of Science, Computer Science",
    },
  ],
  skills: [
    "Python",
    "FastAPI",
    "PostgreSQL",
    "React",
    "TypeScript",
    "Redis",
    "AWS",
    "Kubernetes",
    "REST APIs",
    "System Design",
    "Test Automation",
    "CI/CD",
    "Performance Optimization",
    "Backend Architecture",
  ],
};

function Resume() {
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const { personalInfo, experience, education, skills } = resumeData;

  return (
    <main className="px-6 py-28 text-slate-200 md:px-12 lg:px-24">
      <Helmet>
        <title>Resume | Gabriel E.</title>
      </Helmet>

      <section
        ref={contentRef}
        id="resume"
        className="mx-auto max-w-5xl rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-2xl shadow-slate-950/50 md:p-10"
      >
        <div className="flex flex-col gap-8 border-b border-slate-800 pb-8 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="mb-3 text-sm font-medium uppercase tracking-[0.35em] text-cyan-400">
              Resume
            </p>

            <h1 className="text-4xl font-bold tracking-tight text-slate-100 md:text-5xl">
              {personalInfo.firstName} {personalInfo.lastName}
            </h1>

            <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-400">
              {personalInfo.summary}
            </p>

            <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-400">
              <span>{personalInfo.location}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <a
                href="https://www.linkedin.com/in/gabriel-j-80a715188/"
                target="_blank"
                rel="noreferrer"
                className="text-cyan-300 transition hover:text-cyan-200"
              >
                {personalInfo.linkedin}
              </a>

              <a
                href={`https://${personalInfo.website}`}
                target="_blank"
                rel="noreferrer"
                className="text-cyan-300 transition hover:text-cyan-200"
              >
                {personalInfo.website}
              </a>
            </div>
          </div>

          <button
            onClick={reactToPrintFn}
            aria-label="Download resume"
            className="flex w-fit items-center gap-2 rounded border border-cyan-400 px-5 py-3 text-sm font-medium text-cyan-300 transition hover:bg-cyan-400/10 hover:text-cyan-200"
          >
            <HiDownload size={20} />
            Download
          </button>
        </div>

        <section className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
            Experience
          </h2>

          <div className="mt-6 space-y-8">
            {experience.map((job) => (
              <article
                key={`${job.company}-${job.startDate}`}
                className="group rounded-xl border border-slate-800 bg-slate-950/40 p-5 transition hover:border-cyan-400/40 hover:bg-slate-900/80"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 group-hover:text-cyan-300">
                      {job.position}
                    </h3>

                    <p className="mt-1 text-slate-400">
                      {job.company} · {job.location}
                    </p>
                  </div>

                  <p className="text-sm font-medium text-slate-500">
                    {job.startDate} — {job.endDate}
                  </p>
                </div>

                <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-400">
                  {job.description.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-cyan-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-12 grid gap-10 md:grid-cols-2">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
              Education
            </h2>

            <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950/40 p-5">
              {education.map((item) => (
                <div key={item.institution}>
                  <h3 className="text-lg font-semibold text-slate-100">
                    {item.institution}
                  </h3>

                  <p className="mt-1 text-sm text-slate-400">
                    {item.location}
                  </p>

                  <p className="mt-2 text-sm text-slate-400">{item.degree}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
              Skills
            </h2>

            <div className="mt-6 flex flex-wrap gap-3">
              {skills.map((skill) => (
                <span
                  key={skill}
                  className="rounded-full border border-slate-700 bg-slate-950/50 px-4 py-2 text-sm text-slate-400 transition hover:border-cyan-400/50 hover:text-cyan-300"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </section>
      </section>
    </main>
  );
}

export default Resume;
